import type { DefaultOptionType } from '@/components/combobox/combobox';
import PriceUnitsCombobox from '@/components/combobox/price-units-combobox';
import { useSimulationErrorNotification } from '@/hooks/use-simulation-error-notification';
import {
  SIMULATION_LIST_QUERY_KEY,
  useStartSimulation,
} from '@/pages/price-planning/simulation/simulation-queries';
import { ROUTES } from '@/routes';
import {
  formatDateStringIso,
  getToday,
  parseDateString,
} from '@/utils/date-utils';
import type { NormalizedError } from '@/utils/error-utils';
import {
  Button,
  DatePicker,
  HelpText,
  Label,
  Modal,
  TextField,
} from '@lego/connect-components-react';
import { useQueryClient } from '@tanstack/react-query';
import type { MultiValue } from 'chakra-react-select';
import { addYears, startOfYear } from 'date-fns';
import { type RefObject, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateSimulationName } from './validators';

type Props = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
};

export const NewSimulationModal = ({ isOpen, triggerRef, onClose }: Props) => {
  const queryClient = useQueryClient();
  const showSimulationError = useSimulationErrorNotification();
  const navigate = useNavigate();

  const onMutationSuccess = (simulationId: number) => {
    navigate(`${ROUTES.SimulationTool.staticPath}/${simulationId}`);
    queryClient.invalidateQueries({
      queryKey: [SIMULATION_LIST_QUERY_KEY],
    });
    handleClose();
  };

  const onMutationError = (error: NormalizedError) => {
    showSimulationError(error, 'create simulation');
  };

  const {
    mutateAsync: startSimulationMutation,
    isPending: isCreatingSimulation,
  } = useStartSimulation(onMutationSuccess, onMutationError);

  const [selectedPriceUnits, setSelectedPriceUnits] = useState<
    DefaultOptionType[]
  >([]);
  const [modalNode, setModalNode] = useState<HTMLDivElement | null>(null);
  const [form, setForm] = useState<{
    planningDate: Date | null;
    simulationName: string;
  }>({
    planningDate: getToday(),
    simulationName: '',
  });

  const simulationNameErrors = useMemo(
    () => validateSimulationName(form.simulationName),
    [form.simulationName],
  );

  const canStartSimulation =
    form.planningDate !== null &&
    simulationNameErrors.length === 0 &&
    selectedPriceUnits.length > 0;

  const handleClose = () => {
    onClose();
    setForm({
      planningDate: getToday(),
      simulationName: '',
    });
    setSelectedPriceUnits([]);
  };

  const hasNameError =
    form.simulationName.length > 0 && simulationNameErrors.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      triggerRef={triggerRef}
      ref={setModalNode}
    >
      <Modal.Title>New Simulation</Modal.Title>
      <Modal.Content>
        <PriceUnitsCombobox
          selectedItems={selectedPriceUnits}
          onSelect={(selected: MultiValue<DefaultOptionType>) =>
            setSelectedPriceUnits([...selected])
          }
        />
        <div>
          <Label>Planning date</Label>
          <DatePicker
            mode="single"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            portalTarget={modalNode}
            value={
              form.planningDate ? formatDateStringIso(form.planningDate) : null
            }
            maxDate={formatDateStringIso(startOfYear(addYears(new Date(), 3)))}
            onChange={(_, value) => {
              setForm((prev) => ({
                ...prev,
                planningDate: value ? parseDateString(value) : null,
              }));
            }}
          />
        </div>
        <div>
          <Label>Simulation name</Label>
          <TextField
            id="simulation-name"
            placeholder="Input a unique simulation name"
            value={form.simulationName}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                simulationName: e.target.value,
              }))
            }
            validationState={hasNameError ? 'invalid' : undefined}
          />
          {hasNameError && (
            <HelpText id="simulation-name-error" variant="negative">
              {simulationNameErrors.join(' ')}
            </HelpText>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={handleClose}
          appearance="ghost"
          variant="neutral"
          aria-label={'Cancel'}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            form.planningDate &&
              startSimulationMutation({
                versionName: form.simulationName,
                planningDate: form.planningDate,
                priceUnitIds: selectedPriceUnits.map((pu) => Number(pu.id)),
              });
          }}
          aria-label={'Submit'}
          disabled={!canStartSimulation}
          loading={isCreatingSimulation}
        >
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
